import { useState, useMemo, useRef, useEffect } from 'react'
import {
  Search, ChevronDown, ChevronRight, BookOpen, Terminal as TerminalIcon,
  Bot, Users, TrendingUp, Database, BarChart2, LayoutGrid, Settings,
  Keyboard, Zap, HelpCircle, Copy, Check, Target, Radar,
} from 'lucide-react'
import { clsx } from 'clsx'

// ── Types ─────────────────────────────────────────────────────────────────────

interface Command {
  name: string
  aliases?: string[]
  usage: string
  example: string
  description: string
}

interface CommandCategory {
  title: string
  commands: Command[]
}

interface HelpSection {
  id: string
  title: string
  icon: React.ElementType
  content: React.ReactNode
}

// ── Code Block ────────────────────────────────────────────────────────────────

function CodeBlock({ children }: { children: string }) {
  const [copied, setCopied] = useState(false)
  const copy = () => {
    navigator.clipboard.writeText(children)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }
  return (
    <div className="relative group">
      <pre className="bg-base rounded-lg px-4 py-3 text-sm font-mono text-text-secondary overflow-x-auto border border-border">
        {children}
      </pre>
      <button
        onClick={copy}
        className="absolute top-2 right-2 p-1.5 rounded-md bg-surface-hover text-text-muted opacity-0 group-hover:opacity-100 transition-opacity"
        aria-label="Copy"
      >
        {copied ? <Check size={13} className="text-emerald" /> : <Copy size={13} />}
      </button>
    </div>
  )
}

// ── Collapsible Section ───────────────────────────────────────────────────────

function Collapsible({ id, title, icon: Icon, children, defaultOpen = false, highlight = false }: {
  id: string; title: string; icon: React.ElementType; children: React.ReactNode
  defaultOpen?: boolean; highlight?: boolean
}) {
  const [open, setOpen] = useState(defaultOpen)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (highlight && ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
      setOpen(true)
    }
  }, [highlight])

  return (
    <div ref={ref} id={id} className="card overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-surface-hover/50 transition-colors"
        aria-expanded={open}
        aria-controls={`content-${id}`}
      >
        <div className="w-8 h-8 rounded-lg bg-blue/10 border border-blue/20 flex items-center justify-center shrink-0">
          <Icon size={16} className="text-blue-light" aria-hidden />
        </div>
        <h2 className="flex-1 font-semibold text-text-primary text-sm">{title}</h2>
        {open
          ? <ChevronDown size={16} className="text-text-muted shrink-0" />
          : <ChevronRight size={16} className="text-text-muted shrink-0" />
        }
      </button>
      {open && (
        <div id={`content-${id}`} className="px-5 pb-5 space-y-4 animate-fade-in border-t border-border pt-4">
          {children}
        </div>
      )}
    </div>
  )
}

// ── Subsection ────────────────────────────────────────────────────────────────

function Sub({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-text-primary mb-2">{title}</h3>
      <div className="text-sm text-text-secondary leading-relaxed space-y-2">{children}</div>
    </div>
  )
}

// ── Command Table ─────────────────────────────────────────────────────────────

function CommandTable({ category }: { category: CommandCategory }) {
  return (
    <div>
      <h3 className="panel-title mb-3">{category.title}</h3>
      <div className="overflow-x-auto">
        <table className="table-pro w-full text-sm">
          <thead>
            <tr>
              <th>Command</th>
              <th className="hidden sm:table-cell">Aliases</th>
              <th>Usage</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {category.commands.map(cmd => (
              <tr key={cmd.name}>
                <td className="font-mono text-blue-light whitespace-nowrap">{cmd.name}</td>
                <td className="font-mono text-text-muted text-xs whitespace-nowrap hidden sm:table-cell">
                  {cmd.aliases?.join(', ') || '--'}
                </td>
                <td className="font-mono text-text-secondary text-xs">{cmd.usage}</td>
                <td className="text-text-secondary">{cmd.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ── Command Data ──────────────────────────────────────────────────────────────

const COMMAND_CATEGORIES: CommandCategory[] = [
  {
    title: 'Equity',
    commands: [
      { name: '/quote', aliases: ['/q', '/bq', '/qr'], usage: '/quote AAPL', example: '/quote TSLA', description: 'Real-time stock quote with price, change, volume, market cap' },
      { name: '/chart', aliases: ['/c', '/gp', '/gip', '/comp'], usage: '/chart AAPL 1y', example: '/chart MSFT 6m', description: 'Historical price chart for given period' },
      { name: '/profile', aliases: ['/p', '/des'], usage: '/profile AAPL', example: '/profile NVDA', description: 'Company profile: sector, industry, description, key stats' },
      { name: '/earnings', aliases: ['/e', '/ern', '/ea'], usage: '/earnings AAPL', example: '/earnings GOOG', description: 'Earnings history with EPS estimates vs actual' },
      { name: '/dividends', aliases: ['/div', '/dvd'], usage: '/dividends AAPL', example: '/dividends JNJ', description: 'Dividend history, yield, payout ratio' },
      { name: '/technicals', aliases: ['/ta'], usage: '/technicals AAPL', example: '/technicals AMZN', description: 'Technical analysis: RSI, MACD, moving averages, signals' },
      { name: '/compare', aliases: [], usage: '/compare AAPL MSFT', example: '/compare TSLA RIVN', description: 'Side-by-side comparison of two tickers' },
      { name: '/options', aliases: ['/opt'], usage: '/options AAPL', example: '/options SPY', description: 'Options chain with calls and puts' },
    ],
  },
  {
    title: 'Market',
    commands: [
      { name: '/indices', aliases: ['/idx', '/wb', '/indu', '/nky', '/dax'], usage: '/indices', example: '/indices', description: 'Major global indices (S&P 500, FTSE, DAX, Nikkei, etc.)' },
      { name: '/movers', aliases: ['/mov'], usage: '/movers', example: '/movers', description: 'Top gainers and losers today' },
      { name: '/sectors', aliases: ['/s5sect'], usage: '/sectors', example: '/sectors', description: 'Sector performance heatmap' },
      { name: '/bonds', aliases: ['/gb'], usage: '/bonds', example: '/bonds', description: 'Treasury yield curve' },
      { name: '/vix', aliases: [], usage: '/vix', example: '/vix', description: 'VIX volatility index current level' },
      { name: '/forex', aliases: ['/fx', '/fxip', '/fxc'], usage: '/forex', example: '/forex', description: 'Major forex rates (EUR/USD, GBP/USD, etc.)' },
      { name: '/crncy', aliases: ['/fxcm'], usage: '/crncy', example: '/crncy', description: 'FX cross-rate matrix' },
      { name: '/commodities', aliases: ['/cmdty', '/cmd'], usage: '/commodities', example: '/commodities', description: 'Commodity futures (oil, gold, silver, etc.)' },
      { name: '/country', aliases: [], usage: '/country US', example: '/country UK', description: 'Country economic dashboard' },
      { name: '/topvol', aliases: [], usage: '/topvol', example: '/topvol', description: 'Top volume stocks today' },
      { name: '/economic', aliases: ['/eco', '/ecfc'], usage: '/economic', example: '/economic', description: 'Economic calendar: upcoming releases' },
      { name: '/markets', aliases: [], usage: '/markets', example: '/markets', description: 'Global market sessions (open/closed status)' },
    ],
  },
  {
    title: 'Valuation & Fundamentals',
    commands: [
      { name: '/wacc', aliases: [], usage: '/wacc AAPL', example: '/wacc MSFT', description: 'Weighted average cost of capital' },
      { name: '/dcf', aliases: [], usage: '/dcf AAPL', example: '/dcf GOOG', description: 'Discounted cash flow valuation model' },
      { name: '/ddm', aliases: [], usage: '/ddm JNJ', example: '/ddm KO', description: 'Dividend discount model' },
      { name: '/financials', aliases: ['/fa'], usage: '/financials AAPL', example: '/financials AMZN', description: 'Key financial metrics (revenue, margins, ratios)' },
      { name: '/peers', aliases: [], usage: '/peers AAPL', example: '/peers TSLA', description: 'Peer comparison table' },
      { name: '/targets', aliases: ['/tgt'], usage: '/targets AAPL', example: '/targets NVDA', description: 'Analyst price targets (high, low, mean)' },
      { name: '/ratings', aliases: ['/ratd'], usage: '/ratings AAPL', example: '/ratings META', description: 'Analyst ratings distribution (buy/hold/sell)' },
    ],
  },
  {
    title: 'Alternative Data',
    commands: [
      { name: '/insider', aliases: ['/anr', '/bi'], usage: '/insider AAPL', example: '/insider TSLA', description: 'Insider trades (buys/sells by executives)' },
      { name: '/congress', aliases: [], usage: '/congress AAPL', example: '/congress', description: 'Congressional trading activity' },
      { name: '/guru', aliases: [], usage: '/guru Buffett', example: '/guru Dalio', description: 'Guru/whale portfolio holdings (13F filings)' },
      { name: '/shorts', aliases: ['/si', '/most'], usage: '/shorts AAPL', example: '/shorts GME', description: 'Short interest data' },
      { name: '/squeeze', aliases: [], usage: '/squeeze', example: '/squeeze', description: 'Short squeeze candidates' },
      { name: '/sentiment', aliases: ['/salt'], usage: '/sentiment AAPL', example: '/sentiment TSLA', description: 'Social media sentiment analysis' },
      { name: '/wsb', aliases: [], usage: '/wsb', example: '/wsb', description: 'WallStreetBets trending tickers' },
      { name: '/stocktwits', aliases: ['/st'], usage: '/stocktwits', example: '/stocktwits', description: 'Stocktwits trending tickers' },
      { name: '/social', aliases: [], usage: '/social AAPL', example: '/social TSLA', description: 'Cross-platform sentiment (Reddit + Stocktwits)' },
      { name: '/fear', aliases: [], usage: '/fear', example: '/fear', description: 'Fear & Greed Index' },
      { name: '/flows', aliases: [], usage: '/flows', example: '/flows', description: 'ETF sector rotation flows' },
      { name: '/contracts', aliases: [], usage: '/contracts', example: '/contracts', description: 'Government contracts data' },
      { name: '/patents', aliases: [], usage: '/patents AAPL', example: '/patents GOOG', description: 'Patent activity for a company' },
      { name: '/lobbying', aliases: [], usage: '/lobbying', example: '/lobbying', description: 'Top lobbying spenders' },
      { name: '/trends', aliases: [], usage: '/trends AI', example: '/trends crypto', description: 'Google Trends search interest' },
      { name: '/cramer', aliases: [], usage: '/cramer', example: '/cramer', description: 'Jim Cramer picks (inverse Cramer tracker)' },
      { name: '/splits', aliases: [], usage: '/splits', example: '/splits', description: 'Recent and upcoming stock splits' },
      { name: '/risks', aliases: [], usage: '/risks AAPL', example: '/risks TSLA', description: 'Key risk factors from 10-K filing' },
      { name: '/exec', aliases: [], usage: '/exec AAPL', example: '/exec MSFT', description: 'Executive compensation data' },
      { name: '/revenue', aliases: [], usage: '/revenue AAPL', example: '/revenue AMZN', description: 'Revenue breakdown by segment' },
    ],
  },
  {
    title: 'Options & Volatility',
    commands: [
      { name: '/options-flow', aliases: ['/oflow', '/omon', '/ov'], usage: '/options-flow', example: '/options-flow', description: 'Unusual options flow activity' },
      { name: '/pcr', aliases: [], usage: '/pcr', example: '/pcr', description: 'Put/call ratio' },
      { name: '/maxpain', aliases: [], usage: '/maxpain AAPL', example: '/maxpain SPY', description: 'Options max pain level' },
      { name: '/vol scan', aliases: [], usage: '/vol scan', example: '/vol scan', description: 'Volatility arbitrage scanner (IV vs RV)' },
      { name: '/vol surface', aliases: [], usage: '/vol surface AAPL', example: '/vol surface SPY', description: 'Volatility surface (smile/skew)' },
      { name: '/vol squeeze', aliases: [], usage: '/vol squeeze', example: '/vol squeeze', description: 'Volatility squeeze detector' },
      { name: '/vol term', aliases: [], usage: '/vol term AAPL', example: '/vol term TSLA', description: 'Vol term structure (contango/backwardation)' },
    ],
  },
  {
    title: 'Crypto',
    commands: [
      { name: '/crypto', aliases: [], usage: '/crypto', example: '/crypto', description: 'BTC and ETH prices (default)' },
      { name: '/crypto dashboard', aliases: [], usage: '/crypto dashboard', example: '/crypto dashboard', description: 'Full overview: top 20, dominance, fear & greed' },
      { name: '/crypto top', aliases: ['/ctop'], usage: '/crypto top', example: '/crypto top', description: 'Top 20 coins by market cap' },
      { name: '/crypto detail', aliases: [], usage: '/crypto detail BTC', example: '/crypto detail ETH', description: 'Full coin detail and metrics' },
      { name: '/crypto trending', aliases: [], usage: '/crypto trending', example: '/crypto trending', description: 'Trending coins right now' },
      { name: '/crypto global', aliases: [], usage: '/crypto global', example: '/crypto global', description: 'Global crypto market stats' },
      { name: '/crypto defi', aliases: [], usage: '/crypto defi', example: '/crypto defi', description: 'DeFi statistics' },
      { name: '/crypto exchanges', aliases: [], usage: '/crypto exchanges', example: '/crypto exchanges', description: 'Top exchanges by volume' },
      { name: '/crypto fear', aliases: [], usage: '/crypto fear', example: '/crypto fear', description: 'Crypto Fear & Greed index' },
    ],
  },
  {
    title: 'Portfolio',
    commands: [
      { name: '/portfolio', aliases: ['/pf', '/port'], usage: '/portfolio', example: '/portfolio', description: 'Portfolio summary with holdings and allocation' },
      { name: '/positions', aliases: ['/pos', '/pmon'], usage: '/positions', example: '/positions', description: 'Open positions with P&L' },
      { name: '/risk', aliases: ['/mars', '/var'], usage: '/risk', example: '/risk', description: 'Risk dashboard (VaR, drawdown, exposure)' },
      { name: '/stress', aliases: [], usage: '/stress', example: '/stress', description: 'Portfolio stress test results' },
      { name: '/pot', aliases: [], usage: '/pot', example: '/pot', description: 'Discretionary trading pot balance' },
      { name: '/pnl', aliases: [], usage: '/pnl', example: '/pnl', description: 'Profit & loss summary' },
      { name: '/close', aliases: [], usage: '/close AAPL', example: '/close TSLA', description: 'Close a position' },
      { name: '/goals', aliases: [], usage: '/goals', example: '/goals', description: 'Financial goals and probability of success' },
      { name: '/watchlist', aliases: ['/wl'], usage: '/watchlist', example: '/watchlist', description: 'Current watchlist' },
    ],
  },
  {
    title: 'Agents & AI',
    commands: [
      { name: '/analyze', aliases: ['/run'], usage: '/analyze', example: '/analyze', description: 'Run full agent analysis across all 7 AI agents' },
      { name: '/recommendations', aliases: ['/recs'], usage: '/recommendations', example: '/recommendations', description: 'View pending agent recommendations' },
      { name: '/consensus', aliases: [], usage: '/consensus AAPL', example: '/consensus TSLA', description: 'Agent consensus on a specific ticker' },
      { name: '/phd', aliases: ['/agents', '/swarm'], usage: '/phd', example: '/phd MSFT', description: 'PhD Agent Swarm status or per-ticker consensus (100 AI analysts)' },
      { name: '/agent-scorecard', aliases: ['/as', '/ai-score'], usage: '/agent-scorecard', example: '/agent-scorecard', description: 'AI agent performance scorecard and track records' },
    ],
  },
  {
    title: 'Signal Intelligence',
    commands: [
      { name: '/signals', aliases: [], usage: '/signals', example: '/signals NVDA', description: 'Global signal digest or per-ticker intelligence from 34 feeds' },
      { name: '/leaderboard', aliases: ['/lb', '/top-signals'], usage: '/leaderboard', example: '/leaderboard 48', description: 'Top tickers ranked by multi-source convergence' },
      { name: '/convergences', aliases: [], usage: '/convergences', example: '/convergences', description: 'Multi-source convergence detection (2+ sources agree)' },
      { name: '/intelligence', aliases: ['/intel'], usage: '/intelligence', example: '/intelligence', description: 'Full signal intelligence report across all feeds' },
      { name: '/trade-ideas', aliases: ['/ti'], usage: '/trade-ideas', example: '/trade-ideas', description: 'AI-scored trade ideas with conviction, smart money, Kelly sizing' },
      { name: '/pulse', aliases: [], usage: '/pulse', example: '/pulse', description: 'Quick market pulse — portfolio, F&G, signals, sessions' },
      { name: '/risk-radar', aliases: ['/rr'], usage: '/risk-radar', example: '/risk-radar', description: 'Signal-driven portfolio risk assessment' },
      { name: '/entropy', aliases: ['/signal-quality'], usage: '/entropy', example: '/entropy', description: 'Shannon entropy quality grades per signal source (A/B/C/D)' },
      { name: '/attribution', aliases: ['/attr'], usage: '/attribution', example: '/attribution', description: 'Source accuracy — which signals predicted price moves correctly' },
      { name: '/sectors-signals', aliases: [], usage: '/sectors-signals', example: '/sectors-signals', description: 'Signal momentum by sector — which sectors are trending' },
    ],
  },
  {
    title: 'Strategies & Backtesting',
    commands: [
      { name: '/strategy list', aliases: [], usage: '/strategy list', example: '/strategy list', description: 'List all available backtest strategies' },
      { name: '/strategy', aliases: [], usage: '/strategy momentum AAPL', example: '/strategy mean_reversion SPY', description: 'Quick backtest a strategy on a ticker' },
      { name: '/scan ema_crossover', aliases: [], usage: '/scan ema_crossover', example: '/scan ema_crossover', description: 'Scan watchlist for EMA 8/21 crossovers' },
      { name: '/scan vwap', aliases: [], usage: '/scan vwap', example: '/scan vwap', description: 'Scan for VWAP bounce candidates' },
      { name: '/scan orb', aliases: [], usage: '/scan orb', example: '/scan orb', description: 'Opening range breakout candidates' },
      { name: '/kelly', aliases: [], usage: '/kelly', example: '/kelly', description: 'Kelly criterion position sizing calculator' },
      { name: '/tra', aliases: [], usage: '/tra', example: '/tra', description: 'Trade analysis (win rate, P&L, hold time)' },
    ],
  },
  {
    title: 'Dashboards',
    commands: [
      { name: '/ftse100', aliases: ['/ukx'], usage: '/ftse100', example: '/ftse100', description: 'FTSE 100 dashboard' },
      { name: '/sp500', aliases: ['/spx'], usage: '/sp500', example: '/sp500', description: 'S&P 500 dashboard' },
      { name: '/nasdaq', aliases: [], usage: '/nasdaq', example: '/nasdaq', description: 'NASDAQ dashboard' },
      { name: '/sector', aliases: [], usage: '/sector technology', example: '/sector healthcare', description: 'Sector deep-dive dashboard' },
      { name: '/macro', aliases: ['/wirp', '/wei', '/weco'], usage: '/macro', example: '/macro', description: 'Macro dashboard (GDP, CPI, Fed rates)' },
      { name: '/pd', aliases: [], usage: '/pd', example: '/pd', description: 'Portfolio dashboard' },
      { name: '/screener', aliases: ['/grab', '/eqs', '/secf', '/rv'], usage: '/screener', example: '/screener', description: 'Stock screener results' },
      { name: '/news', aliases: ['/top', '/n', '/ni', '/cn'], usage: '/news', example: '/news', description: 'Latest market headlines' },
      { name: '/calendar', aliases: ['/evts'], usage: '/calendar', example: '/calendar', description: 'Events calendar' },
      { name: '/ipo', aliases: [], usage: '/ipo', example: '/ipo', description: 'Upcoming IPOs' },
    ],
  },
  {
    title: 'ETF & Funds',
    commands: [
      { name: '/etf', aliases: [], usage: '/etf SPY', example: '/etf QQQ', description: 'ETF holdings and sector breakdown' },
      { name: '/fund', aliases: ['/mf', '/perf'], usage: '/fund VFIAX', example: '/fund ARKK', description: 'Fund/ETF performance metrics' },
    ],
  },
  {
    title: 'Prediction Markets',
    commands: [
      { name: '/predictions', aliases: ['/polymarket'], usage: '/predictions', example: '/predictions', description: 'Trending prediction markets' },
      { name: '/elections', aliases: [], usage: '/elections', example: '/elections', description: 'Election and political odds' },
      { name: '/econ-predict', aliases: [], usage: '/econ-predict', example: '/econ-predict', description: 'Economic predictions (Fed, CPI, recession)' },
    ],
  },
  {
    title: 'Market Structure',
    commands: [
      { name: '/cacs', aliases: [], usage: '/cacs AAPL', example: '/cacs TSLA', description: 'Corporate actions (splits, dividends, M&A)' },
      { name: '/spread', aliases: [], usage: '/spread AAPL', example: '/spread SPY', description: 'Bid/ask spread analysis' },
      { name: '/float', aliases: [], usage: '/float AAPL', example: '/float GME', description: 'Float and ownership structure' },
    ],
  },
  {
    title: 'Asset Shortcuts',
    commands: [
      { name: '/gc1', aliases: [], usage: '/gc1', example: '/gc1', description: 'Gold futures' },
      { name: '/cl1', aliases: [], usage: '/cl1', example: '/cl1', description: 'Crude oil futures' },
      { name: '/si1', aliases: [], usage: '/si1', example: '/si1', description: 'Silver futures' },
      { name: '/ng1', aliases: [], usage: '/ng1', example: '/ng1', description: 'Natural gas futures' },
      { name: '/hg1', aliases: [], usage: '/hg1', example: '/hg1', description: 'Copper futures' },
      { name: '/eurusd', aliases: [], usage: '/eurusd', example: '/eurusd', description: 'EUR/USD exchange rate' },
      { name: '/gbpusd', aliases: [], usage: '/gbpusd', example: '/gbpusd', description: 'GBP/USD exchange rate' },
      { name: '/usdjpy', aliases: [], usage: '/usdjpy', example: '/usdjpy', description: 'USD/JPY exchange rate' },
      { name: '/xauusd', aliases: [], usage: '/xauusd', example: '/xauusd', description: 'Gold/USD spot price' },
      { name: '/xagusd', aliases: [], usage: '/xagusd', example: '/xagusd', description: 'Silver/USD spot price' },
    ],
  },
  {
    title: 'Workspace',
    commands: [
      { name: '/layout save', aliases: [], usage: '/layout save MySetup', example: '/layout save trading', description: 'Save current workspace layout' },
      { name: '/layout load', aliases: [], usage: '/layout load MySetup', example: '/layout load trading', description: 'Load a saved workspace layout' },
      { name: '/layout list', aliases: [], usage: '/layout list', example: '/layout list', description: 'List all saved layouts' },
    ],
  },
  {
    title: 'Brokers',
    commands: [
      { name: '/alpaca', aliases: [], usage: '/alpaca', example: '/alpaca', description: 'Alpaca account status and positions' },
    ],
  },
  {
    title: 'System',
    commands: [
      { name: '/help', aliases: [], usage: '/help', example: '/help', description: 'Show terminal command reference' },
      { name: '/wiki', aliases: ['/help-wiki'], usage: '/wiki', example: '/wiki', description: 'Quick guide + link to full help wiki' },
      { name: '/status', aliases: [], usage: '/status', example: '/status', description: 'System status and API health' },
      { name: '/clear', aliases: [], usage: '/clear', example: '/clear', description: 'Clear terminal output (also Ctrl+L)' },
      { name: '/telegram', aliases: [], usage: '/telegram', example: '/telegram', description: 'Telegram notifications setup/status' },
    ],
  },
]

// ── Section Content ───────────────────────────────────────────────────────────

function GettingStartedContent() {
  return (
    <>
      <Sub title="What is AgentVault Capital?">
        <p>
          AgentVault Capital is an AI-powered wealth management platform that combines
          real-time market data, 7 specialised AI agents, alternative data intelligence,
          and a Bloomberg-style command terminal into a single unified dashboard.
          It is designed for sophisticated investors who want institutional-grade analysis
          with the flexibility of a personal trading desk.
        </p>
      </Sub>

      <Sub title="How to log in (MFA setup)">
        <p>
          Navigate to the login page and enter your credentials. On first login, you will
          be prompted to set up Multi-Factor Authentication (MFA) using a TOTP authenticator
          app (Google Authenticator, Authy, or similar). Scan the QR code shown on screen,
          then enter the 6-digit code to verify. MFA is required for all sessions.
        </p>
      </Sub>

      <Sub title="Your first session -- what to do">
        <ol className="list-decimal list-inside space-y-1">
          <li>Review the <strong>Command Centre</strong> dashboard for a market overview</li>
          <li>Open the <strong>Terminal</strong> and try <code className="font-mono text-blue-light">/quote AAPL</code> or <code className="font-mono text-blue-light">/indices</code></li>
          <li>Navigate to <strong>AI Agents</strong> and run a full analysis with <code className="font-mono text-blue-light">/analyze</code></li>
          <li>Check <strong>Recommendations</strong> to see what the agents suggest</li>
          <li>Explore <strong>Intelligence</strong> for alternative data (congressional trades, insider activity)</li>
          <li>Set up your <strong>Watchlist</strong> with tickers you follow</li>
        </ol>
      </Sub>

      <Sub title="Understanding the dashboard">
        <p>
          The Command Centre shows key performance indicators at a glance: portfolio value,
          daily P&L, agent status, market regime, and pending recommendations. Each card
          links to a deeper view. The sidebar provides navigation to all platform sections.
          Real-time data auto-refreshes every 30 seconds.
        </p>
      </Sub>
    </>
  )
}

function AgentsContent() {
  const agents = [
    { name: 'ARIA (Chat Agent)', model: 'Qwen Plus', desc: 'Your conversational AI assistant. Ask questions about your portfolio, markets, or strategies in natural language.' },
    { name: 'Market Agent', model: 'Qwen Plus', desc: 'Monitors real-time market conditions, regime detection (bull/bear/neutral), sector rotation, and macro signals.' },
    { name: 'Screener Agent', model: 'Qwen Turbo', desc: 'Scans the market for opportunities matching your criteria: momentum plays, oversold stocks, value picks, breakout candidates.' },
    { name: 'Portfolio Agent', model: 'DeepSeek Chat', desc: 'Analyses your current holdings, identifies concentration risks, suggests rebalancing, and evaluates new position ideas against your portfolio.' },
    { name: 'Risk Agent', model: 'DeepSeek Chat', desc: 'Calculates VaR, stress tests, drawdown scenarios, correlation risk, and position-level risk metrics. Flags when risk limits are breached.' },
    { name: 'Goals Agent', model: 'DeepSeek Chat', desc: 'Tracks progress toward your financial goals (retirement, property, education). Runs Monte Carlo simulations to estimate probability of success.' },
    { name: 'Compliance Agent', model: 'Qwen Plus', desc: 'Enforces trading rules, position limits, and regulatory constraints. Checks that proposed trades comply with your risk parameters.' },
  ]

  return (
    <>
      <Sub title="How the 7 agents work">
        <p>
          Each agent runs independently as a specialised AI model, either Qwen (for fast advisory tasks)
          or DeepSeek (for deep analytical reasoning). When you trigger an analysis, all 7 agents
          evaluate the current market state and your portfolio simultaneously. Their outputs are
          combined into a consensus view with specific, actionable recommendations.
        </p>
      </Sub>

      <Sub title="Agent roster">
        <div className="grid gap-3">
          {agents.map(a => (
            <div key={a.name} className="bg-base rounded-lg p-3 border border-border">
              <div className="flex items-center gap-2 mb-1">
                <Bot size={14} className="text-blue-light shrink-0" />
                <span className="font-semibold text-text-primary text-sm">{a.name}</span>
                <span className="ml-auto text-[10px] font-mono px-2 py-0.5 rounded-full bg-surface-hover text-text-muted">{a.model}</span>
              </div>
              <p className="text-sm text-text-secondary">{a.desc}</p>
            </div>
          ))}
        </div>
      </Sub>

      <Sub title="How consensus is reached">
        <p>
          After all agents complete their analysis, the system aggregates their signals. Each agent
          contributes a directional score (bullish/bearish/neutral) and confidence level. A recommendation
          is generated only when there is sufficient agreement. High-conviction picks (4+ agents agreeing)
          are flagged as strong signals. Conflicting views are noted for your review.
        </p>
      </Sub>

      <Sub title="How to trigger an analysis">
        <p>
          Use the <code className="font-mono text-blue-light">/analyze</code> command in the terminal, or click
          the "Run Analysis" button on the AI Agents page. Analysis typically takes 10-30 seconds depending
          on market data availability. Results appear in the Advisory and Recommendations pages.
        </p>
      </Sub>
    </>
  )
}

function PersonasContent() {
  const personas = [
    { name: 'Warren Buffett', focus: 'Value investing, economic moats, long-term compounding', style: 'Looks for companies with durable competitive advantages, strong management, and prices below intrinsic value. Favours buy-and-hold with minimal portfolio turnover.' },
    { name: 'Ray Dalio', focus: 'Macro, all-weather portfolio, risk parity', style: 'Emphasises diversification across asset classes, economic cycle analysis, and maintaining balance between growth and inflation hedges. Thinks in terms of economic machines and debt cycles.' },
    { name: 'George Soros', focus: 'Macro trading, reflexivity, currency/commodities', style: 'Looks for boom-bust patterns and market reflexivity. Willing to make large concentrated bets when conviction is high. Focuses on global macro trends and policy shifts.' },
    { name: 'Jim Simons', focus: 'Quantitative, statistical arbitrage, systematic', style: 'Pure data-driven approach using statistical patterns, mean reversion, and momentum factors. Favours high-frequency signals, diversification across many small positions, and rigorous backtesting.' },
    { name: 'Cathie Wood', focus: 'Disruptive innovation, growth, technology', style: 'Invests in companies at the forefront of innovation: AI, genomics, fintech, robotics, energy storage. High conviction, long time horizon, embraces volatility as opportunity.' },
    { name: 'Michael Burry', focus: 'Contrarian, deep value, special situations', style: 'Goes against the crowd. Looks for asymmetric risk/reward in overlooked or distressed situations. Known for identifying bubbles and structural market risks before others.' },
  ]

  return (
    <>
      <Sub title="What are Fund Manager Personas?">
        <p>
          AgentVault tracks the 13F filings of legendary fund managers and makes their portfolio
          data available through the <code className="font-mono text-blue-light">/guru</code> command.
          You can see what these investors are buying and selling, and use their investment philosophies
          as a lens to evaluate your own positions. The Intelligence page shows the latest guru
          portfolio snapshots.
        </p>
      </Sub>

      <Sub title="The 6 personas">
        <div className="grid gap-3">
          {personas.map(p => (
            <div key={p.name} className="bg-base rounded-lg p-3 border border-border">
              <div className="flex items-center gap-2 mb-1">
                <Users size={14} className="text-purple-light shrink-0" />
                <span className="font-semibold text-text-primary text-sm">{p.name}</span>
              </div>
              <p className="text-xs text-text-muted mb-1">{p.focus}</p>
              <p className="text-sm text-text-secondary">{p.style}</p>
            </div>
          ))}
        </div>
      </Sub>

      <Sub title="How to use them">
        <p>
          Run <code className="font-mono text-blue-light">/guru Buffett</code> to see Buffett's latest holdings,
          or <code className="font-mono text-blue-light">/guru Dalio</code> for Dalio's portfolio.
          Compare their positions against your own holdings to find ideas or validate your thesis.
          The Intelligence page aggregates all guru data into one view.
        </p>
      </Sub>
    </>
  )
}

function PlanningContent() {
  const pages = [
    { slug: '/app/profile', name: 'Household Profile', summary: 'The one source of truth — ages, base + bonus salaries, target drawdown, annual savings, PCLS taken, retirement age. Edit here and every planner picks it up. Persists to Postgres.' },
    { slug: '/app/net-worth', name: 'Net Worth', summary: 'Big-picture view: tracked investments + property gross − liabilities = total net worth. Splits by bucket, wrapper, owner, currency, account.' },
    { slug: '/app/cash', name: 'Cash Drag Analyzer', summary: 'Every £ of cash in the household, what it earns, what you\'re losing vs money-market rates. Touchable (discretionary) vs locked (manager-controlled).' },
    { slug: '/app/property', name: 'Property & Mortgages', summary: 'The Gables + 35 Farm Avenue with real mortgage details, LTV, monthly outflow, annual interest, urgent flags (empty BTL bleeding interest).' },
    { slug: '/app/planning', name: 'FIRE Planning', summary: 'Can you stop? Classifies you against Lean/Standard/Fat/Coast FIRE targets. Includes the Build-Up to Retirement chart and the % of property in FIRE pot slider.' },
    { slug: '/app/drawdown', name: 'Drawdown Strategy', summary: '3-bucket retirement withdrawal model (cash/bonds/growth) + tax-optimised blend showing exactly how to source £200k/yr from SIPP + ISA + GIA + state/DB pensions to minimise tax.' },
    { slug: '/app/monthly-income', name: 'Monthly Income Calendar', summary: '12-month grid showing where every £ of retirement income arrives — state pension monthly, SIPP quarterly (Jan/Apr/Jul/Oct), PCLS annually in April, etc.' },
    { slug: '/app/income-projection', name: 'Income Projection', summary: '20-year dividend + interest + staking income forecast from every holding, rolled up by wrapper and owner.' },
    { slug: '/app/monte-carlo', name: 'Monte Carlo FIRE', summary: '10,000 stochastic paths with configurable volatility. Shows success probability at age 95 — the stochastic reality check on the deterministic planner.' },
    { slug: '/app/mc-jurisdictions', name: 'MC × Jurisdictions', summary: 'The killer insight: for the same net target, how much gross do you need in each jurisdiction and what\'s the resulting success probability? Macau 16% vs UK 2% at £200k net.' },
    { slug: '/app/scenarios', name: 'What-If Scenarios', summary: 'Three scenarios: Sell 35 Farm Avenue (with proper PRR + CGT), Sell The Gables + rent abroad (zero CGT), Pay off Gables early (don\'t). Each with live sliders.' },
    { slug: '/app/stacker', name: 'Scenario Stacker', summary: 'Stack multiple levers — sell Farm Ave + downsize Gables + Cyprus + delay retirement + extra savings + reduce spend — and see the compound effect on success probability and IHT.' },
    { slug: '/app/pension', name: 'Pension Optimiser', summary: '£60k AA + carry-forward (3 prior years) + tapered AA for >£260k earners. SIPP vs ISA decision logic for Simon\'s 45% marginal rate.' },
    { slug: '/app/db-pensions', name: 'DB Pensions', summary: 'CRUD for Bank of England DB pension + any other guaranteed income. Consumed by the drawdown optimiser before SIPP drawdown starts.' },
    { slug: '/app/tax', name: 'Tax Planning', summary: 'UK 2025/26 wrappers, allowances, unrealised CGT exposure. Progress bars on ISA £20k, SIPP £60k, CGT £3k, dividend £500.' },
    { slug: '/app/tax-loss', name: 'Tax-Loss Harvest', summary: 'Finds harvestable losses in GIA/Crypto, bed-and-ISA suitability, strategy recommendations. Excludes ISA/SIPP losses (no tax value).' },
    { slug: '/app/iht', name: 'IHT Estimator', summary: 'Current UK IHT bill vs post-April-2027 (SIPPs enter estate). Today ~£631k → April 2027 ~£1.67M cliff edge.' },
    { slug: '/app/iht-mitigation', name: 'IHT Mitigation', summary: 'Models the impact of specific actions: SIPP spenddown before 2027, annual gifts, AIM Business Relief portfolio, charitable bequest (reduces rate 40%→36%), whole-of-life cover in trust.' },
    { slug: '/app/jurisdictions', name: 'Jurisdictions', summary: 'UK / Hong Kong / Singapore / Cyprus / Malta / Macau / Portugal side-by-side. DTA badges show which have UK pension treaty relief. Cyprus currently ranks #1 for SIPP drawdown.' },
    { slug: '/app/relocation', name: 'Relocation Plan', summary: '5-phase 12-18 month checklist to capture the ~£2.5M 20-year tax/IHT prize: preparation → residency → UK tax exit → first drawdown year → long-term maintenance.' },
    { slug: '/app/concentration', name: 'Concentration Risk', summary: 'Surfaces MSFT at 60% of XO SIPP, BTC at 71% of Coinbase, SMT at 62% of HL. Single-stock household + per-account + sector + currency + region breakdowns.' },
    { slug: '/app/rebalancer', name: 'Multi-Account Rebalancer', summary: 'Target allocation → concrete trade list across discretionary accounts only. Picks the most concentrated positions first.' },
    { slug: '/app/models', name: 'Model Portfolios', summary: '9 ETF-backed models (Vanguard LS80/60, 60/40, All-Weather, Permanent, Golden Butterfly, Three-Fund, Aggressive, UK-biased). Feed into Rebalancer end-to-end.' },
    { slug: '/app/alerts', name: 'Smart Alerts', summary: 'Single-page aggregator of every actionable issue across the platform: concentration, cash drag, property urgent, mortgage remortgage, tax allowances unused, IHT exposure, FIRE drift.' },
  ]

  return (
    <>
      <Sub title="Planning & Retirement — session overview">
        <p>
          The platform has a deep retirement/tax/relocation planning stack built for Simon's real
          £4.83M household net worth, £200k target drawdown, and the 21-year age gap with Unman.
          Start at the <strong>Household Profile</strong> page to set your facts, then every
          downstream page automatically picks them up.
        </p>
      </Sub>

      <Sub title="Critical facts baked into the planners">
        <div className="grid gap-2 text-sm">
          <div className="bg-base rounded-lg p-3 border border-border">
            <span className="font-semibold text-text-primary">Simon's PCLS already taken</span>
            <span className="block text-text-muted text-xs mt-1">
              £325,000 crystallised under LTA Fixed Protection. Zero tax-free lump sum
              remaining for the drawdown optimiser.
            </span>
          </div>
          <div className="bg-base rounded-lg p-3 border border-border">
            <span className="font-semibold text-text-primary">Macau has NO DTA with the UK</span>
            <span className="block text-text-muted text-xs mt-1">
              HMRC keeps taxing UK-source SIPP drawdown at source up to 45%. Macau is valuable
              for IHT (£0 estate tax) but not drawdown efficiency — Cyprus wins.
            </span>
          </div>
          <div className="bg-base rounded-lg p-3 border border-border">
            <span className="font-semibold text-text-primary">Portugal NHR closed January 2024</span>
            <span className="block text-text-muted text-xs mt-1">
              Old 10% flat foreign-pension regime no longer available to new applicants.
              Portugal now uses full progressive scale topping at 48% — worse than UK.
            </span>
          </div>
          <div className="bg-base rounded-lg p-3 border border-border">
            <span className="font-semibold text-text-primary">35 Farm Avenue PRR partial coverage</span>
            <span className="block text-text-muted text-xs mt-1">
              Bought Sept 2013, lived in until Dec 2017, rented since. PRR covers ~40% of
              the gain (52 live-in months + 9 final-period months ÷ 152 total). Real CGT
              liability ~£19k, not the ~£40k a naive BTL calc would suggest.
            </span>
          </div>
          <div className="bg-base rounded-lg p-3 border border-border">
            <span className="font-semibold text-text-primary">April 2027 SIPP-IHT cliff</span>
            <span className="block text-text-muted text-xs mt-1">
              SIPPs become part of the estate for UK IHT from April 2027. Simon's bill jumps
              from ~£631k today to ~£1.67M — a £1.04M overnight cliff. Mitigation planner on
              <code className="font-mono text-blue-light">/app/iht-mitigation</code>.
            </span>
          </div>
        </div>
      </Sub>

      <Sub title="All planning pages">
        <div className="grid gap-2">
          {pages.map(p => (
            <div key={p.slug} className="bg-base rounded-lg p-3 border border-border">
              <div className="flex items-baseline gap-2 mb-1 flex-wrap">
                <code className="font-mono text-blue-light text-xs">{p.slug}</code>
                <span className="font-semibold text-text-primary text-sm">{p.name}</span>
              </div>
              <p className="text-xs text-text-secondary">{p.summary}</p>
            </div>
          ))}
        </div>
      </Sub>

      <Sub title="Typical workflow">
        <ol className="list-decimal list-inside space-y-1 text-sm">
          <li>Edit <code className="font-mono text-blue-light">/app/profile</code> — set your target drawdown, retirement age, savings rate</li>
          <li>Check <code className="font-mono text-blue-light">/app/planning</code> — see the Build-Up chart and how close you are</li>
          <li>Check <code className="font-mono text-blue-light">/app/monte-carlo</code> — is deterministic optimism hiding stochastic reality?</li>
          <li>Explore <code className="font-mono text-blue-light">/app/jurisdictions</code> + <code className="font-mono text-blue-light">/app/mc-jurisdictions</code> — where would you retire?</li>
          <li>Use <code className="font-mono text-blue-light">/app/stacker</code> to compound levers: sell Farm Ave + Cyprus + delay 2 years</li>
          <li>Open <code className="font-mono text-blue-light">/app/drawdown</code> to see the tax-optimised blend + 35-year projection</li>
          <li>Review <code className="font-mono text-blue-light">/app/iht-mitigation</code> — the £1M April-2027 cliff, how to soften it</li>
        </ol>
      </Sub>
    </>
  )
}


function TradingContent() {
  return (
    <>
      <Sub title="Paper trading mode">
        <p>
          AgentVault runs in paper trading mode by default. All trades are simulated with real market
          prices but no actual capital is at risk. This lets you test strategies, validate agent
          recommendations, and build confidence before connecting a live broker. Paper trading
          tracks your P&L, win rate, and performance metrics as if the trades were real.
        </p>
      </Sub>

      <Sub title="How recommendations work">
        <ol className="list-decimal list-inside space-y-1">
          <li>AI agents analyse the market and your portfolio</li>
          <li>Recommendations are generated with entry price, target, stop-loss, and rationale</li>
          <li>Recommendations appear on the Recommendations page with status "Pending"</li>
          <li>You review and approve (or reject) each recommendation</li>
          <li>Approved trades are executed in paper trading mode (or via connected broker)</li>
        </ol>
      </Sub>

      <Sub title="Pot rules and risk limits">
        <p>
          Your discretionary trading pot defines the maximum capital available for new positions.
          The system enforces position limits (default: GBP 20,000 per position), maximum portfolio
          concentration (no single stock above 15%), and overall drawdown limits. The Risk Agent
          and Compliance Agent automatically block trades that violate these rules.
        </p>
      </Sub>

      <Sub title="How to approve a trade">
        <p>
          Navigate to the <strong>Recommendations</strong> page. Each pending recommendation shows
          the ticker, direction (long/short), suggested size, entry/target/stop levels, and the
          agents' rationale. Click "Approve" to execute or "Reject" to dismiss. Only the primary
          user (Unman) can approve trades.
        </p>
      </Sub>

      <Sub title="Position management">
        <p>
          View all open positions on the <strong>Trades</strong> page or via <code className="font-mono text-blue-light">/positions</code>.
          To close a position, use <code className="font-mono text-blue-light">/close TICKER</code> or click the
          close button on the Trades page. The system tracks entry price, current price, unrealised P&L,
          and holding period for each position.
        </p>
      </Sub>
    </>
  )
}

function SignalEdgeContent() {
  return (
    <>
      <Sub title="What makes FinanceGenie's signal edge unique">
        <p>
          FinanceGenie runs <strong>100+ originator feeds</strong> concurrently and fuses them
          through a cross-factor convergence detector. The edge isn't any single feed — it's what
          happens when multiple independent sources (insiders, regulators, factor data, options
          flow, sector momentum) all point the same way on the same ticker at the same time.
        </p>
        <p className="mt-2">
          Every ticker that appears on Trade Ideas has passed through the{' '}
          <strong>dual-path candidate pool</strong>: either 2+ independent sources agree, OR one
          of the high-conviction sources (insider_cluster, SEC 8-K, FDA calendar, DoJ enforcement,
          momentum_leaders, squeeze_setups, earnings_drift, gap_scanner) fires on its own.
        </p>
      </Sub>

      <Sub title="Ticker-specific alpha generators">
        <p className="mb-2">These feeds produce concrete 'buy / sell XYZ' recommendations:</p>
        <ul className="list-disc list-inside space-y-1 text-xs">
          <li><strong>insider_cluster</strong> — 3+ distinct insiders buying the same name in 30d
            (weight 0.92). Highest-alpha fundamental signal in the Form 4 / RNS / PDMR stack.</li>
          <li><strong>momentum_leaders</strong> — top/bottom decile risk-adjusted momentum across
            an 80-ticker liquid universe (weight 0.78). Factor premium since Jegadeesh & Titman 1993.</li>
          <li><strong>sector_leaders</strong> — best-in-class relative strength within each GICS
            sector vs its ETF benchmark (weight 0.72).</li>
          <li><strong>squeeze_setups</strong> — high short interest + positive price action = classic
            short-squeeze precursor (weight 0.80).</li>
          <li><strong>earnings_drift (PEAD)</strong> — names that gapped on earnings and continue
            drifting. 60d outperformance bias (weight 0.82). Bernard & Thomas 1989.</li>
          <li><strong>gap_scanner</strong> — 3%+ fresh daily gaps with volume confirmation, filtered
            to be at least 2× the name's average daily move (weight 0.74).</li>
          <li><strong>technical_scanner</strong> — 52-week breakouts, RSI divergences, MACD crosses,
            volume surges across 238 liquid tickers (every 4h).</li>
        </ul>
      </Sub>

      <Sub title="Golden Setups — the apex signal">
        <p>
          The <strong>golden_setup</strong> detector (weight 0.95) is the platform's highest-
          conviction signal. It fires only when ALL of the following align:
        </p>
        <ol className="list-decimal list-inside space-y-1 mt-2 text-xs">
          <li>3+ distinct high-quality sources agreeing on the same ticker in a 72h window</li>
          <li>Average sentiment exceeds |0.4| (clear directional conviction)</li>
          <li>Trade direction is <em>regime-aligned</em> (LONG in risk-on, SHORT in risk-off)</li>
          <li>Ticker is UK-retail tradeable (no CN A-shares / KR / .TWO)</li>
        </ol>
        <p className="mt-2">
          Fires to <code className="font-mono text-amber">#trade-alerts</code> as an amber-colour
          embed with 24h dedup. Every golden setup also writes a row to the signals table so it
          tops the Trade Ideas ranking for the window.
        </p>
      </Sub>

      <Sub title="Macro regime backdrop">
        <p>
          The <strong>composite regime score</strong> (−5 to +5) at{' '}
          <a href="/app/regime" className="text-blue-light hover:underline">/app/regime</a>{' '}
          fuses 6 components: VIX term structure, yield curve, sector breadth, dollar index,
          credit spreads, options skew. 12 regime-family feeds emit supporting signals.
          Regime directly scales the Kelly sizer on Trade Ideas (0.5× in panic, 1.3× in breakout)
          and filters golden_setup direction.
        </p>
      </Sub>

      <Sub title="Idea display hierarchy">
        <ol className="list-decimal list-inside space-y-1 text-xs">
          <li><strong>Setup of the Day</strong> — top-conviction tradeable idea surfaced on the
            Dashboard top banner. Single concrete buy/sell with entry / stop / target / R/R / size.</li>
          <li><strong>Trade Ideas page</strong> — up to 40 ideas (48h window). Each card opens
            with a prominent BUY/SELL action banner showing entry / stop / target / R/R / size
            and the source mix.</li>
          <li><strong>Discord alerts</strong> — sigma spikes (σ≥3) + convergences (3+ sources) +
            book catalysts + golden setups all fire to <code className="font-mono text-blue-light">#trade-alerts</code>,
            regime events to <code className="font-mono text-blue-light">#macro-regime</code>.</li>
        </ol>
      </Sub>
    </>
  )
}

function AltDataContent() {
  return (
    <>
      <Sub title="Available data sources">
        <p>
          AgentVault integrates over 15 alternative data sources to give you an edge beyond traditional
          financial data. All alternative data is accessible through the Intelligence page or via
          terminal commands.
        </p>
      </Sub>

      <Sub title="Congressional trading">
        <p>
          Track stock trades made by US members of Congress. These are disclosed under the STOCK Act
          and can signal upcoming legislation or policy changes that may affect specific sectors.
          Use <code className="font-mono text-blue-light">/congress</code> to see recent trades, or
          <code className="font-mono text-blue-light">/congress AAPL</code> to filter by ticker.
        </p>
      </Sub>

      <Sub title="Insider trading signals">
        <p>
          Monitor insider buying and selling by company executives (CEO, CFO, directors). Insider
          buying is often a bullish signal since executives risk their own capital. Cluster buys
          (multiple insiders buying simultaneously) are especially significant.
          Use <code className="font-mono text-blue-light">/insider AAPL</code>.
        </p>
      </Sub>

      <Sub title="Guru portfolio tracking">
        <p>
          Track 13F filings from legendary investors: Buffett, Dalio, Simons, Griffin, Tepper,
          Ackman and more. See what they are accumulating or trimming.
          Use <code className="font-mono text-blue-light">/guru</code>.
        </p>
      </Sub>

      <Sub title="Options flow and dark pools">
        <p>
          <code className="font-mono text-blue-light">/options-flow</code> shows unusual options activity
          -- large block trades that may indicate institutional positioning.
          <code className="font-mono text-blue-light">/darkpool</code> shows dark pool prints and
          <code className="font-mono text-blue-light">/accumulation</code> detects accumulation patterns.
          <code className="font-mono text-blue-light">/whale</code> tracks whale-sized transactions.
        </p>
      </Sub>

      <Sub title="Social sentiment">
        <p>
          Aggregate sentiment from Reddit (WallStreetBets), Stocktwits, and other social platforms.
          The sentiment score ranges from very bearish to very bullish. Extreme readings can be
          contrarian indicators.
          Use <code className="font-mono text-blue-light">/sentiment TICKER</code>, <code className="font-mono text-blue-light">/wsb</code>,
          or <code className="font-mono text-blue-light">/social TICKER</code>.
        </p>
      </Sub>

      <Sub title="Prediction markets">
        <p>
          <code className="font-mono text-blue-light">/predictions</code> shows trending prediction markets
          (Polymarket and others). <code className="font-mono text-blue-light">/elections</code> tracks
          political odds. <code className="font-mono text-blue-light">/econ-predict</code> shows economic
          event predictions (Fed rate decisions, CPI, recession probability).
        </p>
      </Sub>
    </>
  )
}

function ChartsContent() {
  return (
    <>
      <Sub title="How to use the chart page">
        <p>
          Navigate to <strong>Charts</strong> in the sidebar. Enter a ticker symbol and select a time
          period (1D, 5D, 1M, 3M, 6M, 1Y, 5Y, MAX). Charts display candlestick data with volume
          overlay. You can also access charts via <code className="font-mono text-blue-light">/chart AAPL 1y</code>
          in the terminal.
        </p>
      </Sub>

      <Sub title="Technical indicators available">
        <ul className="list-disc list-inside space-y-1">
          <li>Moving Averages: SMA 20, SMA 50, SMA 200, EMA 8, EMA 21</li>
          <li>RSI (Relative Strength Index) -- overbought/oversold detection</li>
          <li>MACD (Moving Average Convergence Divergence) -- trend and momentum</li>
          <li>Bollinger Bands -- volatility envelopes</li>
          <li>VWAP (Volume Weighted Average Price) -- institutional fair value</li>
          <li>Volume Profile -- price levels with highest trading activity</li>
          <li>ATR (Average True Range) -- volatility measurement</li>
        </ul>
        <p className="mt-2">
          Use <code className="font-mono text-blue-light">/technicals AAPL</code> for a full technical
          analysis summary with signals.
        </p>
      </Sub>

      <Sub title="Backtesting strategies">
        <p>
          The <strong>Backtest</strong> page lets you test trading strategies against historical data.
          Configure the strategy type (momentum, mean reversion, EMA crossover, etc.), ticker,
          date range, and initial capital. Results show total return, Sharpe ratio, max drawdown,
          win rate, and an equity curve chart.
          In the terminal: <code className="font-mono text-blue-light">/strategy momentum AAPL</code>.
        </p>
      </Sub>

      <Sub title="Volatility analysis">
        <p>
          The vol scanner (<code className="font-mono text-blue-light">/vol scan</code>) compares implied
          volatility to realised volatility to find mispriced options. The volatility surface
          (<code className="font-mono text-blue-light">/vol surface AAPL</code>) shows the IV smile/skew
          across strikes and expirations. The squeeze detector
          (<code className="font-mono text-blue-light">/vol squeeze</code>) finds stocks where Bollinger
          Bands are compressing inside Keltner Channels, signalling an imminent big move.
        </p>
      </Sub>
    </>
  )
}

function WorkspaceContent() {
  return (
    <>
      <Sub title="Multi-panel setup">
        <p>
          The Workspace page provides a configurable multi-panel layout. You can display multiple
          data views side by side: terminal, charts, portfolio, watchlist, news, and more. Drag and
          resize panels to create your ideal trading cockpit.
        </p>
      </Sub>

      <Sub title="Preset layouts">
        <p>
          AgentVault comes with built-in presets: "Trading" (chart + terminal + positions),
          "Research" (screener + news + technicals), "Portfolio" (holdings + risk + goals).
          Load a preset from the layout selector dropdown.
        </p>
      </Sub>

      <Sub title="Saving custom workspaces">
        <p>
          Save your current layout with <code className="font-mono text-blue-light">/layout save MySetup</code>.
          Load it later with <code className="font-mono text-blue-light">/layout load MySetup</code>.
          List all saved layouts with <code className="font-mono text-blue-light">/layout list</code>.
          Layouts persist across sessions.
        </p>
      </Sub>

      <Sub title="Auto-refresh">
        <p>
          All panels auto-refresh at configurable intervals (default: 30 seconds for market data,
          60 seconds for portfolio data). You can adjust refresh rates in Settings or disable
          auto-refresh for specific panels.
        </p>
      </Sub>
    </>
  )
}

function SettingsContent() {
  return (
    <>
      <Sub title="API keys needed">
        <p>
          AgentVault uses several API providers. Most have generous free tiers:
        </p>
        <ul className="list-disc list-inside space-y-1 mt-2">
          <li><strong>Qwen API</strong> -- AI agent models (required)</li>
          <li><strong>DeepSeek API</strong> -- Deep analysis agents (required)</li>
          <li><strong>Finnhub</strong> -- Real-time quotes, free tier: 60 calls/min</li>
          <li><strong>OpenBB</strong> -- Enriched financial data (optional, enhances ETF/fund data)</li>
          <li><strong>QuiverQuant</strong> -- Alternative data: congress, lobbying, insider trades</li>
          <li><strong>CoinGecko</strong> -- Crypto data, free tier available</li>
          <li><strong>Unusual Whales</strong> -- Options flow and dark pool data</li>
          <li><strong>Polymarket</strong> -- Prediction market data (free)</li>
        </ul>
      </Sub>

      <Sub title="Discord notifications setup">
        <p>
          Get real-time alerts in your Discord server:
        </p>
        <ol className="list-decimal list-inside space-y-1 mt-2">
          <li>Open Discord and go to your server</li>
          <li>Click the channel you want alerts in &rarr; <strong>Edit Channel</strong> &rarr; <strong>Integrations</strong> &rarr; <strong>Webhooks</strong></li>
          <li>Click <strong>"New Webhook"</strong> &rarr; name it <strong>"AgentVault"</strong> &rarr; copy the webhook URL</li>
          <li>Go to <strong>AgentVault Settings &gt; Notifications &gt; Discord</strong> &rarr; paste the webhook URL</li>
          <li>Click <strong>"Send Test Message"</strong> to verify the connection</li>
          <li>Toggle which alerts you want forwarded: recommendations, trades, risk, and watchlist</li>
        </ol>
        <p className="mt-2">
          You can also configure Discord via the terminal: <code className="font-mono text-blue-light">/discord</code>.
          Alerts are colour-coded: green for buys, red for sells, amber for warnings, and blue for informational messages.
        </p>
      </Sub>

      <Sub title="Telegram notifications setup">
        <p>
          Get real-time alerts on your phone via Telegram:
        </p>
        <ol className="list-decimal list-inside space-y-1 mt-2">
          <li>Open Telegram &rarr; search <strong>@BotFather</strong> &rarr; send <code className="font-mono text-blue-light">/newbot</code></li>
          <li>Follow the prompts to create a bot &rarr; copy the <strong>bot token</strong></li>
          <li>Send a message to your new bot, then visit{' '}
            <code className="font-mono text-blue-light text-xs">https://api.telegram.org/bot&#123;TOKEN&#125;/getUpdates</code>{' '}
            to find your <strong>chat_id</strong>
          </li>
          <li>Go to <strong>AgentVault Settings &gt; Notifications &gt; Telegram</strong> &rarr; enter bot token and chat ID</li>
          <li>Click <strong>"Send Test Message"</strong> to verify the connection</li>
        </ol>
        <p className="mt-2">
          You can also configure Telegram via the terminal: <code className="font-mono text-blue-light">/telegram</code>.
          Alerts include: trade executions, agent recommendations, risk warnings, and price alerts.
        </p>
      </Sub>

      <Sub title="Broker connections">
        <p>
          Connect live brokers to execute trades with real capital:
        </p>
        <ul className="list-disc list-inside space-y-1 mt-2">
          <li><strong>Alpaca</strong> -- US stocks, commission-free. Paper and live modes. Requires API key + secret.</li>
          <li><strong>IG Index</strong> -- UK/EU spread betting and CFDs. Requires API key, account ID, and password.</li>
          <li><strong>Robinhood</strong> -- US stocks and options. Requires username, password, and MFA token.</li>
        </ul>
        <p className="mt-2">
          Configure broker connections in <strong>Settings &gt; Broker Connections</strong>. You can test the
          connection before enabling live trading. Check broker status with <code className="font-mono text-blue-light">/alpaca</code>.
        </p>
      </Sub>
    </>
  )
}

function KeyboardContent() {
  const shortcuts = [
    { keys: ['Up', 'Down'], desc: 'Navigate command history in the terminal' },
    { keys: ['Tab'], desc: 'Autocomplete commands and ticker symbols' },
    { keys: ['Ctrl', 'L'], desc: 'Clear terminal output' },
    { keys: ['Enter'], desc: 'Execute the current command' },
    { keys: ['Esc'], desc: 'Close modals and dialogs' },
  ]

  const chartShortcuts = [
    { keys: ['1D', '5D', '1M', '3M', '6M', '1Y', '5Y'], desc: 'Quick period selectors on chart page' },
  ]

  return (
    <>
      <Sub title="Terminal shortcuts">
        <div className="space-y-2">
          {shortcuts.map(s => (
            <div key={s.desc} className="flex items-center gap-3">
              <div className="flex gap-1">
                {s.keys.map(k => (
                  <kbd key={k} className="px-2 py-1 text-xs font-mono rounded bg-base border border-border text-text-secondary">{k}</kbd>
                ))}
              </div>
              <span className="text-sm text-text-secondary">{s.desc}</span>
            </div>
          ))}
        </div>
      </Sub>

      <Sub title="Chart shortcuts">
        <div className="space-y-2">
          {chartShortcuts.map(s => (
            <div key={s.desc} className="flex items-center gap-3">
              <div className="flex gap-1">
                {s.keys.map(k => (
                  <kbd key={k} className="px-1.5 py-1 text-xs font-mono rounded bg-base border border-border text-text-secondary">{k}</kbd>
                ))}
              </div>
              <span className="text-sm text-text-secondary">{s.desc}</span>
            </div>
          ))}
        </div>
      </Sub>
    </>
  )
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function HelpPage() {
  const [search, setSearch] = useState('')
  const [activeHash, setActiveHash] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  // Read hash on mount
  useEffect(() => {
    const hash = window.location.hash.replace('#', '')
    if (hash) setActiveHash(hash)
  }, [])

  const sections: HelpSection[] = useMemo(() => [
    { id: 'getting-started', title: 'Getting Started', icon: Zap, content: <GettingStartedContent /> },
    { id: 'planning-retirement', title: 'Planning & Retirement', icon: Target, content: <PlanningContent /> },
    { id: 'terminal-commands', title: 'Terminal Commands', icon: TerminalIcon, content: null },
    { id: 'ai-agents', title: 'AI Agents', icon: Bot, content: <AgentsContent /> },
    { id: 'fund-manager-personas', title: 'Fund Manager Personas', icon: Users, content: <PersonasContent /> },
    { id: 'trading', title: 'Trading', icon: TrendingUp, content: <TradingContent /> },
    { id: 'signal-edge', title: 'Signal Edge', icon: Radar, content: <SignalEdgeContent /> },
    { id: 'alternative-data', title: 'Alternative Data', icon: Database, content: <AltDataContent /> },
    { id: 'charts-analysis', title: 'Charts & Analysis', icon: BarChart2, content: <ChartsContent /> },
    { id: 'workspace', title: 'Workspace', icon: LayoutGrid, content: <WorkspaceContent /> },
    { id: 'settings', title: 'Settings & Configuration', icon: Settings, content: <SettingsContent /> },
    { id: 'keyboard-shortcuts', title: 'Keyboard Shortcuts', icon: Keyboard, content: <KeyboardContent /> },
  ], [])

  // Filter sections and commands by search
  const lowerSearch = search.toLowerCase()

  const filteredSections = useMemo(() => {
    if (!lowerSearch) return sections
    return sections.filter(s => {
      if (s.title.toLowerCase().includes(lowerSearch)) return true
      if (s.id === 'terminal-commands') {
        return COMMAND_CATEGORIES.some(cat =>
          cat.title.toLowerCase().includes(lowerSearch) ||
          cat.commands.some(cmd =>
            cmd.name.toLowerCase().includes(lowerSearch) ||
            cmd.description.toLowerCase().includes(lowerSearch) ||
            cmd.aliases?.some(a => a.toLowerCase().includes(lowerSearch))
          )
        )
      }
      return true // keep content sections and let users scan
    })
  }, [lowerSearch, sections])

  const filteredCommands = useMemo(() => {
    if (!lowerSearch) return COMMAND_CATEGORIES
    return COMMAND_CATEGORIES
      .map(cat => ({
        ...cat,
        commands: cat.commands.filter(cmd =>
          cmd.name.toLowerCase().includes(lowerSearch) ||
          cmd.description.toLowerCase().includes(lowerSearch) ||
          cmd.aliases?.some(a => a.toLowerCase().includes(lowerSearch)) ||
          cat.title.toLowerCase().includes(lowerSearch)
        ),
      }))
      .filter(cat => cat.commands.length > 0)
  }, [lowerSearch])

  const totalCommands = COMMAND_CATEGORIES.reduce((sum, cat) => sum + cat.commands.length, 0)

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue/20 border border-blue/30 flex items-center justify-center">
            <BookOpen size={20} className="text-blue-light" aria-hidden />
          </div>
          <div>
            <h1 className="page-title">Help Wiki</h1>
            <p className="text-sm text-text-secondary">
              Everything you need to know about AgentVault Capital
            </p>
          </div>
        </div>
        <span className="text-[10px] font-mono px-2 py-1 rounded-full bg-surface-hover text-text-muted">
          {totalCommands} commands
        </span>
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" aria-hidden />
        <input
          ref={inputRef}
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search commands, topics, features..."
          className="input pl-10 pr-4 py-3"
          aria-label="Search help"
        />
        {search && (
          <button
            onClick={() => { setSearch(''); inputRef.current?.focus() }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary text-xs"
            aria-label="Clear search"
          >
            Clear
          </button>
        )}
      </div>

      {/* Quick nav */}
      {!search && (
        <div className="flex flex-wrap gap-2">
          {sections.map(s => (
            <a
              key={s.id}
              href={`#${s.id}`}
              onClick={() => setActiveHash(s.id)}
              className="text-xs px-3 py-1.5 rounded-full bg-surface-card border border-border text-text-secondary hover:bg-surface-hover hover:text-text-primary transition-colors"
            >
              {s.title}
            </a>
          ))}
        </div>
      )}

      {/* Sections */}
      {filteredSections.map(section => {
        if (section.id === 'terminal-commands') {
          return (
            <Collapsible
              key={section.id}
              id={section.id}
              title={`${section.title} (${totalCommands})`}
              icon={section.icon}
              defaultOpen={activeHash === section.id || !!search}
              highlight={activeHash === section.id}
            >
              <p className="text-sm text-text-secondary mb-4">
                Bloomberg-compatible aliases are shown in the Aliases column. All commands
                start with <code className="font-mono text-blue-light">/</code>. Type them in the
                Terminal page or any command bar.
              </p>
              {filteredCommands.map(cat => (
                <CommandTable key={cat.title} category={cat} />
              ))}
              {filteredCommands.length === 0 && (
                <p className="text-sm text-text-muted text-center py-4">
                  No commands match "{search}"
                </p>
              )}
            </Collapsible>
          )
        }

        return (
          <Collapsible
            key={section.id}
            id={section.id}
            title={section.title}
            icon={section.icon}
            defaultOpen={activeHash === section.id || !!search}
            highlight={activeHash === section.id}
          >
            {section.content}
          </Collapsible>
        )
      })}

      {/* Footer */}
      <div className="text-center py-6 border-t border-border">
        <p className="text-xs text-text-muted">
          AgentVault Capital -- AI-Powered Wealth Management Platform
        </p>
        <p className="text-xs text-text-muted mt-1">
          Use <code className="font-mono text-blue-light">/help</code> in the terminal for a quick command reference
        </p>
      </div>
    </div>
  )
}
